import { Session } from "@supabase/supabase-js";

export interface Host {
  id: string;
  host_id: string | null;
  name: string;
  meta: {
    age?: string;
    dob?: string;
    zip?: string;
    city?: string;
    state?: string;
    gender?: string;
    about?: string;
    email?: string;
    mobile?: string;
    pronouns?: string;
    lanugages?: string; 
    rate_card?: string;
    experience?: string;
    focus_area?: string;
    top_skus_hosted?: string;
    skills?: string[];
    interests?: string[];
    image?: string;
  };
  created_at: string;
  updated_at: string | null;
}

export interface HostsResponse {
  data: Host[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  message: string;
}

export interface HostsQueryParams {
  page?: number;
  limit?: number;
  city?: string;
  state?: string;
  gender?: string;
  languages?: string[];
  searchText?: string;
}

export const fetchHosts = async (
  params: HostsQueryParams = {},
  session?: Session | null
): Promise<HostsResponse> => {
  if (!session?.access_token) {
    throw new Error("Authentication required");
  }

  const queryParams = new URLSearchParams();
  
  // Add pagination parameters
  if (params.page) queryParams.append("page", params.page.toString());
  if (params.limit) queryParams.append("limit", params.limit.toString());
  
  // Add filter parameters
  if (params.city) queryParams.append("city", params.city);
  if (params.state) queryParams.append("state", params.state);
  if (params.gender) queryParams.append("gender", params.gender);
  if (params.searchText) queryParams.append("searchText", params.searchText);
  
  // Add languages (can be multiple)
  if (params.languages && params.languages.length > 0) {
    params.languages.forEach(lang => queryParams.append("languages", lang));
  }

  const response = await fetch(
    `${import.meta.env.VITE_CC_API_URL}/v1/hosts?${queryParams.toString()}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${session.access_token}`
      }
    }
  );

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || "Failed to fetch hosts");
  }

  return await response.json();
};
